// Contextual help popover copy — one entry per major surface. Keeps help text
// out of the seven components that render <HelpHint>.
export const HELP_HINTS = {
  capture: {
    title: "Why these shot checklists?",
    body: "The checklist walks you through the surfaces landlords most often dispute — walls, floors, and fixtures. Complete coverage means stronger evidence and nothing forgotten.",
    slug: "guided-capture-and-checklists",
  },
  compare: {
    title: "What do these classifications mean?",
    body: "Our AI compares move-in and move-out photos and labels each room: No Change, Normal Wear (everyday aging), or New Damage. Review the photos yourself before relying on a result.",
    slug: "ai-comparison-classifications",
  },
  share: {
    title: "How links & view receipts work",
    body: "Share a read-only report — your landlord needs no account. You get a view receipt when it's opened, and you can revoke the link anytime.",
    slug: "share-links-view-receipts",
  },
  baseline: {
    title: "What is a baseline acknowledgment?",
    body: "A link you send at move-in so your landlord can review and sign off on the starting condition. Agreeing early means far less to argue about at move-out.",
    slug: "baseline-acknowledgment",
  },
  disputeLetter: {
    title: "How letter generation works",
    body: "We draft a dispute letter from your documented photos and move-in/out comparison results. You can edit every line before you send it. It's a document tool, not legal advice.",
    slug: "dispute-letters",
  },
  smallClaims: {
    title: "What's in the packet",
    body: "A well-organized bundle: a demand letter, your photos as exhibits, your move-in/out comparison results, a delivery log, and a chain-of-custody summary — built from your timeline.",
    slug: "small-claims-packet",
  },
  deadline: {
    title: "Where this deadline comes from",
    body: "You enter your deposit-return deadline date yourself. TenantSeal doesn't supply or calculate a legal deadline — check your lease or your local housing resources, then enter the date so we can track it and remind you.",
    slug: "deadline-tracking-state-guides",
  },
};

// Short inline term explanations (1–2 sentences) shown via the small <InfoTip> icon.
export const TERM_TIPS = {
  evidenceStrength: {
    title: "Documentation Thoroughness",
    body: "How complete and organized your rental documentation is. Higher percentages mean more thorough records — not legal strength or a promise of any specific outcome.",
  },
  protectionPlan: {
    title: "Deposit Protection Plan",
    body: "A guided checklist showing the next steps to complete your rental documentation and strengthen your records.",
  },
  missingCoverage: {
    title: "Missing Coverage",
    body: "Important photos that may still be missing, like ceilings, floors, appliance interiors, or under-sink areas.",
  },
  chainOfCustody: {
    title: "Chain of Custody",
    body: "Verification details such as timestamps, hashes, and location signals that help show when and how evidence was captured.",
  },
  landlordAcknowledgment: {
    title: "Landlord Acknowledgment",
    body: "A record that the landlord or manager viewed or acknowledged the move-in baseline.",
  },
  evidencePackage: {
    title: "Evidence Package",
    body: "A professional PDF that organizes your photos, notes, timestamps, comparisons, and verification details.",
  },
  evidenceVault: {
    title: "Evidence Vault",
    body: "A downloadable offline copy of your photos, reports, hashes, and metadata.",
  },
  disputePackage: {
    title: "Dispute preparation",
    body: "Tools that help you organize and draft a deposit dispute — a deduction auditor, an editable dispute-letter draft, and a PDF appendix of your exhibits. A document tool, not legal advice.",
  },
  smallClaimsPacket: {
    title: "Escalation Documentation Packet",
    body: "A structured PDF of your timeline, labeled exhibits, chain-of-custody summary, and an editable demand-letter draft, for escalation if you choose. A document you prepare — not legal advice or a court filing.",
  },
  tamperEvident: {
    title: "Tamper-evident",
    body: "Designed to show if records were changed after being locked.",
  },
  baseline: {
    title: "Baseline",
    body: "The documented move-in condition used for later comparison.",
  },
  deadlineTracking: {
    title: "Deadline Tracking",
    body: "Tracks important deposit-return dates based on the selected state or rule.",
  },
  lockDocumentation: {
    title: "Lock Documentation",
    body: "Locks your documentation with timestamps and integrity verification, helping demonstrate that your records have not been modified after creation.",
  },
  evidencePdf: {
    title: "Evidence PDF",
    body: "Generates a professional PDF containing your photos, notes, timestamps, condition reports, and supporting evidence for sharing or record keeping.",
  },
  shareDocumentation: {
    title: "Share Documentation",
    body: "Creates a secure, revocable link that lets your landlord view your documentation without creating an account.",
  },
  documentationCoverage: {
    title: "Documentation coverage",
    body: "How many of TenantSeal's recommended room photos you've captured for this property. Add photos to the rooms that still need attention to raise it. It reflects thoroughness only — not legal sufficiency or any dispute outcome.",
  },
  aiReviewFlags: {
    title: "AI review flags",
    body: "Photos our AI suggests you look at again. These are suggestions for you to review yourself — not findings, proof, or conclusions about damage, fault, or wear. Open each photo and decide what to keep.",
  },
  exhibitDossier: {
    title: "Exhibit Dossier",
    body: "A PDF that organizes your own records — photos, notes, timestamps, and verification details — into numbered exhibits. A document you prepare, not a certified or court-ready filing.",
  },
  chargeStoryboard: {
    title: "Charge Storyboard",
    body: "Lets you place your own sealed photos and notes next to a charge your landlord listed. TenantSeal preserves the text and never decides whether a charge is fair or valid.",
  },
  portfolioProperties: {
    title: "Properties",
    body: "Your real properties, including archived ones, which stay in your portfolio history. Archived properties don't occupy your free plan's one active-property slot, so you can always start a new one.",
  },
  moveOutCountdown: {
    title: "Move-Out Countdown Plan",
    body: "A guided runway from your lease-end date to full move-out documentation. Nothing is sent to anyone — it's your private checklist. Next: enter your lease-end date, then work through each step.",
  },
  moveoutPrep: {
    title: "Move-out prep plan",
    body: "An AI-suggested list of things to clean, patch, re-photograph, or ask about before you move out. These are suggestions you review; nothing is sent to your landlord. Next: generate the plan and tick items off.",
  },
  inspectionRequest: {
    title: "Pre-move-out inspection request",
    body: "Drafts an email asking your landlord to schedule a walkthrough before you move out. It's emailed to your landlord only when you review it and press Send — never automatically. Next: fill in the details and recipient, review, then send.",
  },
  forwardingNotice: {
    title: "Forwarding address notice",
    body: "Prepares a formal notice telling your landlord where to send your deposit and any statement. It only creates a PDF — nothing is sent for you. Next: enter your forwarding address, download the PDF, and send it by certified mail.",
  },
  depositDeadline: {
    title: "Deposit-return deadline",
    body: "The date you should get your deposit back by. You enter it yourself — TenantSeal doesn't calculate a legal deadline, and reminders go only to you, never your landlord. Next: check your lease or local rules, then enter the date.",
  },
  propertyJourney: {
    title: "Property journey",
    body: "A read-only timeline built automatically from your activity — captures, locks, shares, and comparisons. Nothing is sent. Use it to review your rental's history at a glance.",
  },
  premiumTools: {
    title: "TenantSeal Plus tools",
    body: "Premium tools for organizing a deposit response — deduction auditor, dispute-letter drafting, exhibit dossier, and camera comparison. They prepare documents for you to review and send; nothing is sent automatically. Next: open a tool to start a draft.",
  },
};


// Landing-page FAQ — short, factual answers consistent with existing disclaimer language.
export const LANDING_FAQ = [
  {
    q: "Is this legal advice?",
    a: "No. TenantSeal is a documentation and document-preparation tool, not a law firm, and it does not provide legal advice.",
  },
  {
    q: "Do I need my landlord's permission?",
    a: "No. You're documenting the condition of your own rental and your own photos. Sharing a report with your landlord is optional and entirely your choice.",
  },
  {
    q: "What happens to my photos?",
    a: "Your photos and reports are stored securely and are private to you. Nothing is visible to anyone else unless you choose to share a read-only link.",
  },
  {
    q: "How does the AI comparison work?",
    a: "It compares your move-in and move-out photos of each room and labels them No Change, Normal Wear, or New Damage. Results are AI-generated and may not be fully accurate, so review the photos yourself.",
  },
  {
    q: "Is it free?",
    a: "Yes — documenting your rental, running comparisons, sharing reports, and generating a dispute letter are free to start, with no credit card required during the current period.",
  },
  {
    q: "What if I already moved in?",
    a: "You can still start today. Document the current condition right away, then document again at move-out so you have a clear before-and-after record.",
  },
  {
    q: "Does it work offline?",
    a: "Yes. Photos taken without a signal are saved on your device and upload automatically once you're back online, keeping their original capture time.",
  },
  {
    q: "Where do deposit deadlines come from?",
    a: "You enter your own deposit-return deadline date. TenantSeal doesn't supply or calculate a legal deadline — check your lease or your local housing resources, then enter the date so we can track it and remind you.",
  },
];
